import { trackProductAction } from '@/libs/product-analytics';
import { toaster } from '@/components/ui/toaster';
import api from '@/libs/axiosInstance';
import type { AddToWishlistRequest, ApiResponse, WishlistCreator } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

const addToWishlist = async (data: AddToWishlistRequest): Promise<ApiResponse<WishlistCreator>> => {
  const response = await api.post<ApiResponse<WishlistCreator>>('/dashboard/wishlist', data);
  return response.data;
};

export const useAddToWishlistAction = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<WishlistCreator>, AxiosError<ApiResponse>, AddToWishlistRequest>({
    mutationFn: (input) => trackProductAction('favorites', 'add', () => addToWishlist(input)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wishlist'] });

      toaster.success({
        title: t('wishlist.actions.addSuccess.title'),
        description: t('wishlist.actions.addSuccess.description'),
      });
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || t('common.errors.unexpected');

      toaster.error({
        title: t('wishlist.actions.addError.title'),
        description: errorMessage,
      });
    },
  });
};
